// Identificacao da linguagem a partir do arquivo enviado.
//
// A equipe envia um arquivo; a linguagem sai da extensao. O backend continua
// recebendo { language, sourceCode } — o upload e inteiramente do lado do
// navegador, sem endpoint novo.

import { LANGUAGE_LABEL, MAX_SOURCE_CODE_BYTES, type Language } from "@compila/shared";

/** Extensoes aceitas, por linguagem. */
export const EXTENSIONS: Record<Language, string[]> = {
  PYTHON: [".py"],
  JAVA: [".java"],
  C: [".c"],
  CPP: [".cpp", ".cc", ".cxx", ".c++"],
  JAVASCRIPT: [".js", ".mjs"],
};

/** Atributo `accept` do <input type="file">. */
export const ACCEPT_ATTRIBUTE = Object.values(EXTENSIONS).flat().join(",");

export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot).toLowerCase();
}

export function languageOf(filename: string): Language | null {
  const ext = extensionOf(filename);
  for (const [language, exts] of Object.entries(EXTENSIONS)) {
    if (exts.includes(ext)) return language as Language;
  }
  return null;
}

export { LANGUAGE_LABEL };

/** Conta bytes UTF-8 — o limite do servidor e em bytes, nao em caracteres. */
export function byteSize(text: string): number {
  return new TextEncoder().encode(text).length;
}

export interface FileProblem {
  kind: "error" | "warning";
  message: string;
}

/**
 * Problemas detectaveis antes do envio.
 *
 * O caso do Java nao e detalhe: o Judge0 grava o arquivo como `Main.java`, e
 * uma classe publica com outro nome nao compila. Sem este aviso, a equipe
 * receberia um CE que parece erro do juiz e gastaria minutos da prova
 * procurando defeito num codigo correto.
 */
export function inspectFile(
  filename: string,
  content: string,
  language: Language | null,
): FileProblem[] {
  const problems: FileProblem[] = [];

  if (!language) {
    problems.push({
      kind: "error",
      message: `Extensao "${extensionOf(filename) || "(nenhuma)"}" nao reconhecida. Aceitas: ${ACCEPT_ATTRIBUTE}`,
    });
    return problems;
  }

  const size = byteSize(content);
  if (size > MAX_SOURCE_CODE_BYTES) {
    problems.push({
      kind: "error",
      message: `O arquivo tem ${formatBytes(size)}; o limite e ${formatBytes(MAX_SOURCE_CODE_BYTES)}.`,
    });
  }

  if (content.trim().length === 0) {
    problems.push({ kind: "error", message: "O arquivo esta vazio." });
  }

  if (content.includes("\u0000")) {
    problems.push({
      kind: "error",
      message: "Isto parece um arquivo binario, nao codigo-fonte.",
    });
  }

  if (language === "JAVA") {
    const publica = /public\s+(?:final\s+|abstract\s+)?class\s+(\w+)/.exec(content);
    if (publica && publica[1] !== "Main") {
      problems.push({
        kind: "error",
        message:
          `A classe publica se chama "${publica[1]}". O juiz compila o arquivo ` +
          `como Main.java, entao ela precisa se chamar "Main" — renomeie a ` +
          `classe (e o arquivo, de preferencia) antes de enviar.`,
      });
    } else if (!publica && !/class\s+Main\b/.test(content)) {
      problems.push({
        kind: "warning",
        message: "Nao encontrei uma classe Main. O juiz executa Main.main(String[]).",
      });
    }
  }

  return problems;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}
